import { Link } from "react-router-dom";
import { useWishlist } from "@/context/WishlistContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Heart, Trash2, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function Wishlist() {
    const { items, remove } = useWishlist();
    const { format } = useCurrency();
    const { add } = useCart();

    return (
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12" data-testid="wishlist-page">
            <div className="flex items-center gap-3 mb-8">
                <Heart className="w-7 h-7 text-primary" />
                <h1 className="font-display text-4xl md:text-5xl font-bold">Your wishlist</h1>
            </div>

            {items.length === 0 ? (
                <div className="bg-surface border border-border p-12 text-center" data-testid="wishlist-empty">
                    <Heart className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <h2 className="font-display text-xl font-bold mb-2">No saved items yet</h2>
                    <p className="text-muted-foreground mb-6">Tap the heart on any product to save it here.</p>
                    <Link to="/products" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold hover:bg-primary-hover">
                        Browse products
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {items.map((p) => (
                        <div key={p.product_id} className="group bg-white border border-border" data-testid={`wishlist-item-${p.slug}`}>
                            <Link to={`/products/${p.slug}`}>
                                <div className="aspect-square overflow-hidden bg-surface">
                                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                </div>
                            </Link>
                            <div className="p-4">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{p.category}</div>
                                <Link to={`/products/${p.slug}`}>
                                    <h3 className="font-display font-semibold text-sm leading-tight line-clamp-2 mb-2 hover:text-primary">{p.name}</h3>
                                </Link>
                                <div className="font-display font-bold text-lg mb-3">{format(p.price)}</div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => { add({ id: p.product_id, name: p.name, price: p.price, image_url: p.image_url, slug: p.slug }, 1); toast.success("Added to cart"); }}
                                        className="flex-1 inline-flex items-center justify-center gap-1 bg-primary text-white px-3 py-2 text-xs font-semibold hover:bg-primary-hover"
                                        data-testid={`wishlist-add-cart-${p.slug}`}
                                    >
                                        <ShoppingCart className="w-3 h-3" /> Add
                                    </button>
                                    <button
                                        onClick={() => remove(p.product_id)}
                                        className="px-3 py-2 border border-border hover:border-primary hover:text-primary text-xs"
                                        data-testid={`wishlist-remove-${p.slug}`}
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
